import React, { useState, useRef, useEffect } from 'react';
import { Button } from 'js/design_system/Button';
import { Icon } from 'js/design_system/Icon';
import styled from 'astroturf/react';

const Container = styled.div`
  position: relative;
  display: inline-block;
`;

const Dropdown = styled.div`
  position: absolute;
  top: calc(100% + 4px);
  right: 0;
  min-width: 200px;
  background-color: white;
  border: 1px solid #e1e4e8;
  border-radius: 8px;
  box-shadow: 0 8px 24px rgba(149, 157, 165, 0.2);
  z-index: 1000;
  padding: 4px 0;
`;

const DropdownItem = styled.button`
  width: 100%;
  padding: 8px 16px;
  border: none;
  background: none;
  text-align: left;
  font-size: 14px;
  color: #24292e;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: space-between;

  &:hover {
    background-color: #f6f8fa;
  }

  &.active {
    background-color: #0366d6;
    color: white;
  }

  &:disabled {
    color: #6a737d;
    cursor: not-allowed;
    opacity: 0.6;
  }
`;

export type ClaimFilter = 'all' | 'essential' | 'optional';

interface ClaimFilterPanelProps {
  onFilterChange?: (filter: ClaimFilter) => void;
  currentFilter?: ClaimFilter;
  disabled?: boolean;
  essentialCount?: number;
  optionalCount?: number;
}

const ClaimFilterPanel: React.FC<ClaimFilterPanelProps> = ({
  onFilterChange,
  currentFilter = 'all',
  disabled = false,
  essentialCount = 0,
  optionalCount = 0,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isOpen) {
        setIsOpen(false);
        buttonRef.current?.focus();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen]);

  // Filter options with counts
  const filterOptions = [
    {
      value: 'all' as ClaimFilter,
      label: 'All Claims',
      count: essentialCount + optionalCount,
    },
    {
      value: 'essential' as ClaimFilter,
      label: 'Essential Only',
      count: essentialCount,
    },
    {
      value: 'optional' as ClaimFilter,
      label: 'Optional Only',
      count: optionalCount,
    },
  ];

  // Get current filter display text for button
  const getCurrentFilterText = () => {
    if (currentFilter === 'all') return 'All Claims';
    if (currentFilter === 'essential') return 'Essential Claims';
    if (currentFilter === 'optional') return 'Optional Claims';
    return 'All Claims';
  };

  // Handle filter selection
  const handleFilterSelect = (filter: ClaimFilter) => {
    if (onFilterChange) {
      onFilterChange(filter);
    }
    setIsOpen(false);
  };

  return (
    <Container ref={containerRef}>
      {/* Filter Button */}
      <Button
        ref={buttonRef}
        onClick={() => !disabled && setIsOpen(!isOpen)}
        size="xs"
        icon={<Icon name="filter_list" size={16} />}
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        aria-label={`Filter claims: ${getCurrentFilterText()}`}
        disabled={disabled}
      >
        <span>{getCurrentFilterText()}</span>
        <Icon name="expand_more" size={16} style={{ marginLeft: '4px' }} />
      </Button>

      {/* Dropdown */}
      {isOpen && !disabled && (
        <Dropdown role="listbox">
          {filterOptions.map((option) => (
            <DropdownItem
              key={option.value}
              onClick={() => handleFilterSelect(option.value)}
              className={option.value === currentFilter ? 'active' : ''}
              role="option"
              aria-selected={option.value === currentFilter}
            >
              <span>{option.label}</span>
              <span>({option.count})</span>
            </DropdownItem>
          ))}
        </Dropdown>
      )}
    </Container>
  );
};

export default ClaimFilterPanel;
